import { NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	TemplateRef,
	computed,
	inject,
	input
} from '@angular/core';
import { CommonColors } from '@inscomponents/core';
import { match, P } from 'ts-pattern';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'side' | 'start' | 'end';

export interface TooltipData {
	text: string | string[];
	color?: CommonColors;
	delay?: number;
	position?: TooltipPosition;
}

@Component({
	selector: 'skl-tooltip',
	template: `
		<div class="tooltip-inner">
			@if (templateRef(); as tpl) {
				<ng-container *ngTemplateOutlet="tpl" />
			} @else {{{ textContent() }}}
		</div>
	`,
	styles: `
		:host {
			--_tooltip-background: var(--surface-1-color-dark);
			--_tooltip-text-color: var(--text-color-light);
			--_tooltip-border-color: color-mix(in oklab, var(--surface-1-color-dark), white 15%);
			--_translate-x: 0px;
			--_translate-y: -4px;

			box-sizing: border-box;
			container-type: anchored;
			pointer-events: none;
			position: fixed;
			margin: 0;
			inset: auto;
			font-size: 0.875rem;
			padding: .25rem .5rem;
			white-space: pre-line;
			text-wrap: pretty;
			overflow-wrap: anywhere;
			overflow: hidden;
			background-color: var(--_tooltip-background);
			color: var(--_tooltip-text-color);
			border: 1px solid var(--_tooltip-border-color);
			border-radius: .25rem;
			width: fit-content;
			max-width: 65ch;
			box-shadow:
				0px 1px 2px hsl(0deg 0% 0% / 10%),
				0px 3px 6px hsl(0deg 0% 0% / 10%),
				0px 9px 18px hsl(0deg 0% 0% / 10%),
				0px 27px 54px hsl(0deg 0% 0% / 10%);

			opacity: 0;
			scale: 0.96;
			transform: translate(var(--_translate-x), var(--_translate-y));
			transition:
				opacity 100ms cubic-bezier(0.4, 0, 1, 1),
				scale 100ms cubic-bezier(0.4, 0, 1, 1),
				transform 100ms cubic-bezier(0.4, 0, 1, 1),
				display 100ms allow-discrete,
				overlay 100ms allow-discrete;
		}

		:host([data-positioning='anchor']) {
			position-area: bottom;
			position-try-fallbacks: flip-block, flip-inline;
			margin-block-start: 6px;
		}

		:host([data-positioning='anchor'][data-position='top']) {
			position-area: top;
			position-try-fallbacks: flip-block, flip-inline;
			margin-block-end: 6px;
			margin-block-start: 0;
		}

		:host([data-positioning='anchor'][data-position='side']),
		:host([data-positioning='anchor'][data-position='right']),
		:host([data-positioning='anchor'][data-position='end']) {
			position-area: inline-end;
			position-try-fallbacks: flip-inline, flip-block;
			margin-inline-start: 6px;
			margin-block-start: 0;
		}

		:host([data-positioning='anchor'][data-position='left']),
		:host([data-positioning='anchor'][data-position='start']) {
			position-area: inline-start;
			position-try-fallbacks: flip-inline, flip-block;
			margin-inline-end: 6px;
			margin-block-start: 0;
		}

		:host([data-position='top']) {
			--_translate-x: 0px;
			--_translate-y: 4px;
		}

		:host([data-position='side']),
		:host([data-position='right']),
		:host([data-position='end']) {
			--_translate-x: -4px;
			--_translate-y: 0px;
		}

		:host([data-position='left']),
		:host([data-position='start']) {
			--_translate-x: 4px;
			--_translate-y: 0px;
		}

		:host(:popover-open) {
			opacity: 1;
			scale: 1;
			transform: translate(0, 0);
			transition:
				opacity 200ms cubic-bezier(0.16, 1, 0.3, 1),
				scale 220ms cubic-bezier(0.2, 1.12, 0.4, 1),
				transform 200ms cubic-bezier(0.16, 1, 0.3, 1),
				display 240ms allow-discrete,
				overlay 240ms allow-discrete;

			@starting-style {
				opacity: 0;
				scale: 0.94;
				transform: translate(var(--_translate-x), var(--_translate-y));
			}
		}

		@media (prefers-reduced-motion: reduce) {
			:host,
			:host(:popover-open) {
				transition: none;
				animation: none;
				scale: 1;
				transform: none;
			}

			:host(:popover-open) {
				@starting-style {
					opacity: 1;
					scale: 1;
					transform: none;
				}
			}
		}

		:host([data-color]) {
			--_tooltip-background: light-dark(
				color-mix(in oklab, var(--_color-source), white 20%),
				color-mix(in oklab, var(--_color-source), black 65%)
			);
			--_tooltip-text-color: light-dark(
				color-mix(in oklab, var(--_color-source), black 80%),
				color-mix(in oklab, var(--_color-source), white 97%)
			);
			--_tooltip-border-color: light-dark(
				color-mix(in oklab, var(--_color-source), black 5%),
				color-mix(in oklab, var(--_color-source), black 55%)
			);

			@media (prefers-contrast: more) {
				--_tooltip-background: light-dark(
					color-mix(in oklab, var(--_color-source), white 95%),
					color-mix(in oklab, var(--_color-source), black 85%)
				);
				--_tooltip-text-color: light-dark(
					color-mix(in oklab, var(--_color-source), black 90%),
					color-mix(in oklab, var(--_color-source), white 90%)
				);
				--_tooltip-border-color: var(--_color-source);
			}
		}

		:host([data-color='error']) {
			--_color-source: var(--error-color-light);
		}

		:host([data-color='warning']) {
			--_color-source: var(--warning-color-light);
		}

		:host([data-color='success']) {
			--_color-source: var(--success-color-light);
		}

		:host([data-color='info']) {
			--_color-source: var(--info-color-light);
		}

		:host([data-color='primary']) {
			--_color-source: var(--primary-color-light);
		}

		:host([data-color='secondary']) {
			--_color-source: var(--secondary-color-light);
		}

		:host([data-color='accent']) {
			--_color-source: oklch(75% 0.16 43.76);
		}

		`,
		host: {
			'[attr.popover]': '"manual"',
			'[attr.role]': '"tooltip"',
			'[attr.id]': 'id()',
			'[attr.data-positioning]': 'anchorName() ? "anchor" : null',
			'[attr.data-position]': 'position()',
			'[style.position-anchor]': 'anchorName() || null',
			'[class.tooltip]': 'true',
			'[attr.data-color]': 'color()',
		},
		imports: [NgTemplateOutlet],
		changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SklTooltipComponent {
	readonly elementRef = inject(ElementRef<HTMLElement>);

	readonly content = input<string | TemplateRef<any> | TooltipData | string[] | null>(null);
	readonly id = input<string>('');
	readonly anchorName = input<string | null>(null);
	readonly position = input<TooltipPosition>('bottom');

	protected readonly templateRef = computed(() => {
		const val = this.content();
		return val instanceof TemplateRef ? val : null;
	});

	protected readonly textContent = computed(() =>
		match(this.content())
			.with(P.string, (str) => str)
			.with(P.array(P.string), (arr) => arr.join('\n'))
			.with({ text: P.string }, (data) => data.text)
			.with({ text: P.array(P.string) }, (data) => data.text.join('\n'))
			.otherwise(() => '')
	);

	protected readonly color = computed<CommonColors | null>(() =>
		match(this.content())
			.with({ color: P.select(P.nonNullable) }, (color) => color)
			.otherwise(() => null)
	);

	show() {
		this.elementRef.nativeElement.showPopover?.();
	}

	hide() {
		this.elementRef.nativeElement.hidePopover?.();
	}
}
